<script lang="ts">
  import { onMount } from "svelte";
  import { auth, showToast, handleSessionExpired } from "../lib/stores";
  import * as pb from "../lib/pocketbase";
  import type { Role, UserRecord } from "../lib/types";

  let users: UserRecord[] = [];
  let loadError = "";

  let inviteEmail = "";
  let invitePassword = "";
  let inviteRole: Role = "viewer";
  let inviteError = "";

  const roles: Role[] = ["viewer", "editor", "admin"];

  async function refresh() {
    try {
      users = await pb.listUsers($auth);
      loadError = "";
    } catch (e) {
      if (e instanceof pb.SessionExpiredError) { handleSessionExpired(); return; }
      loadError = "Couldn't load users.";
      console.warn(e);
    }
  }

  onMount(refresh);

  function selectValue(e: Event): string {
    return (e.currentTarget as HTMLSelectElement).value;
  }

  async function changeRole(u: UserRecord, role: string) {
    try {
      await pb.updateUserRole($auth, u.id, role);
      showToast(`Updated ${u.email}`);
      if ($auth && $auth.record.id === u.id) {
        const restored = await pb.refreshAuth();
        if (restored) auth.set(restored);
      }
      await refresh();
    } catch (e) {
      if (e instanceof pb.SessionExpiredError) { handleSessionExpired(); return; }
      showToast(`Couldn't update ${u.email}`);
    }
  }

  async function removeUser(u: UserRecord) {
    try {
      await pb.deleteUser($auth, u.id);
      await refresh();
    } catch (e) {
      if (e instanceof pb.SessionExpiredError) { handleSessionExpired(); return; }
      showToast(`Couldn't remove ${u.email}`);
    }
  }

  async function invite() {
    inviteError = "";
    if (!inviteEmail || !invitePassword) { inviteError = "Email and password are required."; return; }
    if (invitePassword.length < 8) { inviteError = "Password needs at least 8 characters."; return; }
    try {
      await pb.inviteUser($auth, inviteEmail, invitePassword, inviteRole);
      showToast(`Added ${inviteEmail}`);
      inviteEmail = ""; invitePassword = "";
      await refresh();
    } catch (e) {
      if (e instanceof pb.SessionExpiredError) { handleSessionExpired(); return; }
      inviteError = e instanceof Error ? e.message : "Couldn't add user.";
    }
  }
</script>

<div class="tab-panel group">
  <div class="group-head">
    <span class="group-tag users">Users</span>
    <span class="group-desc">Admin only. Viewers can look but not touch; editors can edit activities, milestones and risks; admins can also manage accounts here.</span>
  </div>

  <section>
    <div class="section-head"><div class="section-title">Accounts</div></div>
    {#if loadError}
      <div class="modal-hint" style="color:var(--status-critical);">{loadError}</div>
    {/if}
    <div id="userTableScroll">
      <table class="acttable usertable">
        <thead><tr><th style="min-width:220px">Email</th><th style="min-width:120px">Role</th><th></th></tr></thead>
        <tbody>
          {#each users as u (u.id)}
            <tr>
              <td>{u.email}</td>
              <td>
                <select value={u.role} on:change={(e) => changeRole(u, selectValue(e))}>
                  {#each roles as r}
                    <option value={r} selected={u.role === r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                  {/each}
                </select>
              </td>
              <td>
                {#if !$auth || u.id !== $auth.record.id}
                  <button class="rowbtn" title="Remove user" type="button" on:click={() => removeUser(u)}>✕</button>
                {/if}
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  </section>

  <section>
    <div class="section-head"><div class="section-title">Add a user</div></div>
    <div class="invite-row">
      <label>Email<input type="text" bind:value={inviteEmail} placeholder="name@example.com" /></label>
      <label>Password<input type="password" bind:value={invitePassword} placeholder="min. 8 characters" /></label>
      <label>Role
        <select bind:value={inviteRole}>
          <option value="viewer">Viewer</option>
          <option value="editor">Editor</option>
          <option value="admin">Admin</option>
        </select>
      </label>
      <button class="btn primary" type="button" on:click={invite}>Add user</button>
    </div>
    <div class="modal-hint" style="color:var(--status-critical);">{inviteError}</div>
  </section>
</div>
